<!-- Copyright by the Spark Development Network; Licensed under the Rock Community License -->
<template>
    <BaseAsyncPicker v-if="!isHidden"
        v-model="internalValue"
        v-bind="standardProps"
        :items="actualItems" />
</template>

<script setup lang="ts">
    import { computed, PropType, ref, watch } from "vue";
    import BaseAsyncPicker from "@Obsidian/Controls/baseAsyncPicker";
    import { Guid } from "@Obsidian/Types";
    import { useInvokeBlockAction } from "@Obsidian/Utility/block";
    import { standardAsyncPickerProps, useStandardAsyncPickerProps, useVModelPassthrough } from "@Obsidian/Utility/component";
    import { GroupAttendanceDetailGetGroupLocationsRequestBag } from "@Obsidian/ViewModels/Blocks/Group/GroupAttendanceDetail/groupAttendanceDetailGetGroupLocationsRequestBag";
    import { ListItemBag } from "@Obsidian/ViewModels/Utility/listItemBag";

    const invokeBlockAction = useInvokeBlockAction();

    const props = defineProps({
        modelValue: {
            type: Object as PropType<ListItemBag | ListItemBag[] | null>,
            required: false
        },

        groupGuid: {
            type: String as PropType<Guid | null | undefined>,
            default: null
        },

        hideIfEmpty: {
            type: Boolean as PropType<boolean>,
            default: false
        },

        ...standardAsyncPickerProps
    });

    const emit = defineEmits<{
        (e: "update:modelValue", _value: ListItemBag | ListItemBag[] | null): void
    }>();

    // #region Values

    const internalValue = useVModelPassthrough(props, "modelValue", emit);
    const standardProps = useStandardAsyncPickerProps(props);
    const loadedItems = ref<ListItemBag[] | null>(null);

    // #endregion

    // #region Computed Values

    /**
     * The actual items to make available to the picker. This allows us to do any
     * post-processing, such as adding additional items, and still be lazy loaded as well.
     */
    const actualItems = computed((): ListItemBag[] | (() => Promise<ListItemBag[]>) => {
        return loadedItems.value || loadOptions;
    });

    const isHidden = computed(() => props.hideIfEmpty && (!actualItems.value || !actualItems.value.length));

    // #endregion

    // #region Functions

    /**
     * Loads the items from the server.
     */
    const loadOptions = async (): Promise<ListItemBag[]> => {
        const bag: GroupAttendanceDetailGetGroupLocationsRequestBag = {
            groupGuid: props.groupGuid
        };

        const result = await invokeBlockAction<ListItemBag[]>("GetGroupLocations", { bag });

        if (result.isSuccess && result.data) {
            loadedItems.value = result.data;
            return result.data;
        }
        else {
            console.error(result.errorMessage ?? "Unknown error while loading data.");
            loadedItems.value = [];
            return [];
        }
    };

    // #endregion

    // #region Watchers

    /**
     * Clears the loaded items whenever the Group changes so that the items have to be loaded again.
     */
    watch(() => [props.groupGuid], () => {
        loadedItems.value = null;
    });

    // #endregion


</script>